import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Info, Plus } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import useAdminLoad from "@/hooks/useAdminLoad";
import { Empty, LoadState, PageHeader, Panel } from "@/components/admin/AdminUI";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import InterestRow, { labelError } from "@/components/admin/InterestRow";
import { api } from "@/lib/api";

const move = (list, from, to) => {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

function AddInterest({ onAdded }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const invalid = text.trim() ? labelError(text) : null;
  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onAdded(await api("/admin/interests", { body: { label: text.trim(), active: true } }));
      setText("");
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  return (
    <Panel className="mb-6 p-5">
      <form onSubmit={submit} className="flex flex-col gap-3 sm:flex-row sm:items-start" data-testid="interest-add-form">
        <div className="flex-1">
          <label htmlFor="new-interest" className="sr-only">New interest</label>
          <Input id="new-interest" value={text} maxLength={40} onChange={(e) => { setText(e.target.value); setError(null); }} placeholder="Add an interest, like “Board games”"
            className="h-11 rounded-xl" aria-invalid={!!(invalid || error)} data-testid="interest-add-input" />
          {(invalid || error) && <p className="cw-error mt-2 text-xs" role="alert" data-testid="interest-add-error">{error || invalid}</p>}
        </div>
        <Button type="submit" className="h-11 rounded-xl" disabled={busy || !text.trim() || !!invalid} data-testid="interest-add-button"><Plus />{busy ? "Adding…" : "Add interest"}</Button>
      </form>
    </Panel>
  );
}

export default function Interests() {
  const { data, error, loading, reload, setData } = useAdminLoad("/admin/interests");
  const [order, setOrder] = useState([]);
  const [dragFrom, setDragFrom] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);
  const max = data?.max_per_user ?? 5;

  // Keep an unsaved local order when only fields change; reset when the set of interests changes.
  useEffect(() => {
    const items = data?.items || [];
    const byId = new Map(items.map((x) => [x.id, x]));
    setOrder((prev) => (prev.length === items.length && prev.every((x) => byId.has(x.id)) ? prev.map((x) => byId.get(x.id)) : items));
  }, [data]);
  const dirty = order.map((x) => x.id).join() !== (data?.items || []).map((x) => x.id).join();

  const patch = async (it, body) => {
    try {
      setData(await api(`/admin/interests/${it.id}`, { method: "PATCH", body }));
      toast.success(body.label ? "Interest renamed." : body.active ? "Interest turned on." : "Interest turned off.");
      return null;
    } catch (e) { toast.error(e.message); return e.message; }
  };
  const del = async () => {
    setData(await api(`/admin/interests/${deleting.id}`, { method: "DELETE" }));
    toast.success("Interest deleted.");
  };
  const saveOrder = async () => {
    setSaving(true);
    try {
      setData(await api("/admin/interests/order", { method: "PUT", body: { ids: order.map((x) => x.id) } }));
      toast.success("Order saved.");
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const active = order.filter((x) => x.active).length;
  return (
    <div data-testid="admin-interests-page" className={dirty ? "pb-24" : ""}>
      <PageHeader title="Interests" description="The interests people can pick for their profile, in the order they're shown." />
      <div className="cw-bg-tint mb-6 flex items-start gap-3 rounded-2xl px-4 py-3 text-sm" data-testid="interests-note">
        <Info size={18} className="cw-brand-deep mt-0.5 shrink-0" aria-hidden />
        <p className="cw-brand-deep">Deactivated interests stay on profiles that already picked them. Users can pick up to {max}.</p>
      </div>
      <AddInterest onAdded={(res) => { setData(res); toast.success("Interest added."); }} />
      <LoadState loading={loading} error={error} onRetry={reload}>
        <Panel className="overflow-hidden">
          <p className="cw-muted border-b border-border px-5 py-3 text-xs" data-testid="interests-count">{order.length} interests · {active} on · click a label to rename · drag or use the arrows to reorder</p>
          {order.length ? (
            <ol className="divide-y divide-border" data-testid="interests-list">
              {order.map((it, i) => (
                <InterestRow key={it.id} it={it} index={i} total={order.length} dragging={dragFrom === i}
                  onMove={(to) => setOrder((o) => move(o, i, to))} onPatch={(body) => patch(it, body)} onDelete={() => setDeleting(it)}
                  drag={{ start: () => setDragFrom(i), end: () => setDragFrom(null), over: () => { if (dragFrom !== null && dragFrom !== i) { setOrder((o) => move(o, dragFrom, i)); setDragFrom(i); } } }} />
              ))}
            </ol>
          ) : <Empty title="No interests yet" text="Add the first one above." testId="interests-empty" />}
        </Panel>
      </LoadState>
      {dirty && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-white/90 backdrop-blur-md lg:left-64" data-testid="interests-order-bar">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-3 sm:px-8">
            <p className="cw-soft text-sm" role="status">The order changed. Save to apply it.</p>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setOrder(data.items)} disabled={saving} data-testid="interests-order-discard-button">Discard</Button>
              <Button onClick={saveOrder} disabled={saving} data-testid="interests-order-save-button">{saving ? "Saving…" : "Save order"}</Button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} testId="interest-delete-confirm-dialog"
        title={`Delete “${deleting?.label}”?`} description="No profile uses it, so it's removed for good." confirmLabel="Delete interest" onConfirm={del} />
    </div>
  );
}
